import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PROJECTS, PROJECT_CATEGORIES } from "../lib/siteData";
import { ArrowUpRight, MapPin, Calendar } from "lucide-react";
import Reveal from "./Reveal";

/**
 * Featured Work / Projects grid.
 * Props:
 *  - limit (number) — optional cap on items shown
 *  - showFilters (bool) — show category chips
 *  - title — section title
 *  - overline — small label above title
 *  - subtitle — supporting line
 *  - id — optional anchor id
 *  - showViewAll — render a "view all" link
 */
export default function Portfolio({
  limit,
  showFilters = true,
  title = "Featured Work that sets the standard across the Caribbean.",
  overline = "FEATURED WORK",
  subtitle,
  id,
  background = "white",
  showViewAll = false,
}) {
  const [active, setActive] = useState("All");

  const filtered = useMemo(() => {
    const list = active === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === active);
    return typeof limit === "number" ? list.slice(0, limit) : list;
  }, [active, limit]);

  return (
    <section
      id={id}
      className={`relative py-20 lg:py-28 ${background === "muted" ? "bg-[#F8FAFC]" : "bg-white"}`}
      data-testid="portfolio-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#123E73]" />
            <span className="text-xs tracking-[0.3em] font-semibold text-[#123E73]">{overline}</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-10 lg:mb-14">
            <div className="max-w-3xl">
              <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-[#0A192F]">
                {title}
              </h2>
              {subtitle && (
                <p className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed">{subtitle}</p>
              )}
            </div>
            {showFilters && (
              <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
                {PROJECT_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActive(cat)}
                    className={`px-4 py-2 text-xs sm:text-sm font-semibold tracking-wide transition-all duration-300 border ${
                      active === cat
                        ? "bg-[#0A192F] text-white border-[#0A192F]"
                        : "bg-transparent text-[#0A192F] border-slate-300 hover:border-[#0A192F]"
                    }`}
                    data-testid={`portfolio-filter-${cat.toLowerCase()}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-testid="portfolio-grid">
          {filtered.map((p, i) => (
            <Reveal key={p.title} delay={i * 70}>
              <article
                className="group relative bg-white border border-slate-200 overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(10,25,47,0.35)] hover:border-[#123E73] h-full flex flex-col"
                data-testid={`project-card-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {/* Image — show the project photo as-is (titles are baked in) */}
                <div className="relative aspect-[1/1] sm:aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                  />
                </div>

                {/* Content */}
                <div className="p-6 lg:p-8 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="text-[10px] tracking-[0.3em] font-bold text-[#F5A400]">
                      {p.scope.toUpperCase()}
                    </span>
                    <span className="text-[10px] tracking-[0.25em] font-semibold text-slate-500 border border-slate-200 px-2 py-1">
                      {p.category.toUpperCase()}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl lg:text-[26px] font-bold text-[#0A192F] leading-tight tracking-tight">
                    {p.title}
                  </h3>
                  <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-slate-500 text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" />
                      {p.location}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {p.year}
                    </span>
                  </div>
                  <p className="mt-4 text-sm text-slate-600 leading-relaxed flex-1">{p.summary}</p>
                  <Link
                    to="/contact"
                    className="mt-6 inline-flex items-center gap-2 text-[#123E73] font-semibold text-sm group/cta"
                    data-testid={`project-cta-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span>Discuss a similar project</span>
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-1 group-hover/cta:-translate-y-1" />
                  </Link>
                </div>

                {/* Accent corner line */}
                <span className="absolute top-0 right-0 h-0.5 w-0 bg-[#F5A400] transition-all duration-500 group-hover:w-full" />
              </article>
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="py-20 text-center text-slate-500 text-sm" data-testid="portfolio-empty">
            No projects in this category yet.
          </div>
        )}

        {showViewAll && (
          <div className="mt-12 flex justify-center" data-testid="portfolio-view-all-wrap">
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 bg-[#0A192F] text-white hover:bg-[#123E73] font-bold px-8 py-4 transition-all duration-300 hover:-translate-y-0.5"
              data-testid="portfolio-view-all"
            >
              View All Projects
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
